import { useCallback, useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/core';

const STORAGE_KEY = 'soliloquy-doc';
const DEBOUNCE_MS = 500;

export interface PersistenceBlob {
  version: 1;
  title: string;
  metadata: {
    author: string;
    contact: { email?: string; phone?: string; address?: string };
    copyright: string;
    time: string;
    place: string;
  };
  lastSaved: string;
  document: any;
}

export type SaveStatus =
  | { state: 'idle' }
  | { state: 'saving' }
  | { state: 'saved'; timestamp: string }
  | { state: 'error'; message: string };

function createEmptyBlob(title: string): PersistenceBlob {
  return {
    version: 1,
    title,
    metadata: {
      author: '',
      contact: {},
      copyright: '',
      time: '',
      place: '',
    },
    lastSaved: new Date().toISOString(),
    document: null,
  };
}

export function usePersistence(editor: Editor | null, title: string) {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>({ state: 'idle' });
  const [metadata, setMetadata] = useState<PersistenceBlob['metadata']>({
    author: '',
    contact: {},
    copyright: '',
    time: '',
    place: '',
  });
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasUnsavedRef = useRef(false);
  const titleRef = useRef(title);
  titleRef.current = title;

  const save = useCallback(() => {
    if (!editor) return;

    try {
      const json = editor.getJSON();
      const blob: PersistenceBlob = {
        version: 1,
        title: titleRef.current,
        metadata,
        lastSaved: new Date().toISOString(),
        document: json,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(blob));
      hasUnsavedRef.current = false;
      setSaveStatus({
        state: 'saved',
        timestamp: new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
      });
    } catch (err: any) {
      setSaveStatus({
        state: 'error',
        message: err?.name === 'QuotaExceededError' ? 'Storage full' : 'Save failed',
      });
    }
  }, [editor, metadata]);

  const debouncedSave = useCallback(() => {
    hasUnsavedRef.current = true;
    setSaveStatus({ state: 'saving' });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(save, DEBOUNCE_MS);
  }, [save]);

  // Subscribe to editor updates
  useEffect(() => {
    if (!editor) return;
    editor.on('update', debouncedSave);
    return () => {
      editor.off('update', debouncedSave);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [editor, debouncedSave]);

  // Immediate save (Ctrl+S)
  const immediateSave = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    save();
  }, [save]);

  // Load on mount
  const load = useCallback((): PersistenceBlob | null => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const blob = JSON.parse(raw) as PersistenceBlob;
      if (blob?.document) {
        if (blob.metadata) setMetadata(blob.metadata);
        return blob;
      }
    } catch {
      // Corrupted data — start fresh
    }
    return null;
  }, []);

  return { saveStatus, save: immediateSave, load, metadata, setMetadata };
}
